/**
 * ② DASHBOARDS & ANALYTICS — PERSONA-DRIVEN
 * ESGCommandCenter, ComplianceHeatmap, GroupHierarchyPanel, RegulatoryShockPanel,
 * PeerBenchmarkPanel, SimulationWorkbench
 * Every number clickable → drill to the thing it counts
 */
import { useState, useEffect, useCallback } from "react";
import { C, api, post, Btn, Card, Label, Val, Badge, Err, MetricBar, Loader,
         Breadcrumb, DrillRow, DetailPanel, TabBar, HistoryTab, EmptyState, KPICard, LinkedEntity } from "./shared";

// ═══════════════════════════════════════════════════════════
// ESG COMMAND CENTER — "What do I need to do today?"
// ═══════════════════════════════════════════════════════════
export function ESGCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("home");
  const [sel, setSel] = useState(null);
  const [reqDetail, setReqDetail] = useState(null);
  const [tab, setTab] = useState("Overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entity, gaps, drafts, claims, evidence, chain, targets, suppliers, campaigns, connectors, ah] = await Promise.all([
        api("/entity/profile").catch(() => ({})),
        api("/compliance/gaps").catch(() => ({})),
        api("/drafts/claims").catch(() => ({ drafts: [] })),
        api("/claims/list").catch(() => ({ claims: [] })),
        api("/evidence/").catch(() => ({ evidence: [] })),
        api("/events/verify-chain").catch(() => ({})),
        api("/targets/").catch(() => ({ targets: [] })),
        api("/supplier/suppliers").catch(() => ({ suppliers: [] })),
        api("/supplier/campaigns").catch(() => ({ campaigns: [] })),
        api("/connectors/").catch(() => ({ connectors: [] })),
        api("/ai/hallucination/status").catch(() => ({})),
      ]);
      setData({ entity, gaps, drafts: drafts.drafts || [], claims: claims.claims || [], evidence: evidence.evidence || [],
        chain, targets: targets.targets || [], suppliers: suppliers.suppliers || [],
        campaigns: campaigns.campaigns || [], connectors: connectors.connectors || connectors.total || 0, ah });
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader text="Loading command center..." />;
  if (!data) return <Err error="Failed to load dashboard data" />;

  const g = data.gaps;
  const coveragePct = g.total_requirements ? Math.round((g.covered / g.total_requirements) * 100) : 0;
  const pendingDrafts = data.drafts.filter(d => d.status !== "promoted" && d.status !== "rejected");

  const drillGap = async (gap) => {
    setSel(gap); setView("gap-detail"); setTab("Overview"); setReqDetail(null);
    try { setReqDetail(await api(`/requirements/${gap.req_id}`)); } catch (e) {}
  };

  return (<div data-testid="esg-command-center">
    <Breadcrumb path={[
      { label: "Command Center", onClick: () => { setView("home"); setSel(null); } },
      ...(view !== "home" ? [{ label: view === "gaps" ? "Gaps" : view === "claims" ? "Claims" : view === "gap-detail" ? "Gap Detail" : view }] : []),
      ...(sel ? [{ label: sel.ref_code || sel.req_id?.slice(0, 12) || sel.draft_id?.slice(0, 12) || "Detail" }] : [])
    ]} />

    {view === "home" && <>
      <div style={{ padding: "10px 14px", borderRadius: 8, background: `linear-gradient(135deg, ${C.blue}10, ${C.teal}10)`, border: `1px solid ${C.blue}22`, marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{data.entity?.profile?.name || data.entity?.name || "Entity"}</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
          {data.entity?.profile?.sector || data.entity?.sector} · {data.entity?.profile?.jurisdiction || data.entity?.jurisdiction} · {((data.entity?.profile?.employee_count || data.entity?.employee_count || 0)).toLocaleString()} employees · €{(((data.entity?.profile?.revenue_eur || data.entity?.revenue_eur || 0)) / 1e9).toFixed(1)}B revenue
        </div>
      </div>

      <Label>YOUR PRIORITIES TODAY</Label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        <KPICard label="Gaps to Close" value={g.gaps || 0} color={C.red}
          onClick={() => setView("gaps")} sub={`${g.total_requirements || 0} total requirements`} />
        <KPICard label="Claims to Review" value={pendingDrafts.length} color={C.amber}
          onClick={() => setView("claims")} sub={`${data.drafts.length} total drafts`} />
        <KPICard label="Evidence Uploaded" value={data.evidence.length} color={C.blue}
          onClick={() => setView("evidence")} sub="Upload more to close gaps" />
        <KPICard label="Coverage" value={`${coveragePct}%`} color={coveragePct > 50 ? C.emerald : C.red}
          sub={`${g.covered || 0} of ${g.total_requirements || 0} covered`} />
      </div>

      <MetricBar label={`Disclosure Readiness — ${coveragePct}% of ${g.total_requirements || 288} requirements`} val={coveragePct / 100} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
        <Card color={C.emerald} onClick={() => setView("audit")}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><Label>Audit Chain</Label><Val color={data.chain?.valid ? C.emerald : C.red}>{data.chain?.valid ? "VALID" : "CHECK"}</Val></div>
            <div style={{ fontSize: 10, color: C.muted }}>{data.chain?.total_events || 0} events verified</div>
          </div>
        </Card>
        <Card color={C.purple}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><Label>AI Agents</Label><Val color={C.purple}>{data.ah?.total_active || 5} active</Val></div>
            <div style={{ fontSize: 10, color: C.emerald }}>5 AH layers · Zero tolerance</div>
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
        <Card><Label>Targets</Label><Val>{data.targets.length}</Val></Card>
        <Card><Label>Suppliers</Label><Val>{data.suppliers.length}</Val></Card>
        <Card><Label>Campaigns</Label><Val>{data.campaigns.length}</Val></Card>
        <Card><Label>Connectors</Label><Val>{typeof data.connectors === "number" ? data.connectors : data.connectors.length || 0}</Val></Card>
      </div>

      {(g.top_actions || []).length > 0 && <div style={{ marginTop: 14 }}>
        <Label>TOP PRIORITY ACTIONS</Label>
        {g.top_actions.slice(0, 3).map((a, i) => (
          <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 11 }}>
            <span style={{ color: C.red, fontWeight: 700, marginRight: 6 }}>#{i + 1}</span>{a}
          </div>
        ))}
        <Btn onClick={() => setView("gaps")} color={C.red} small>View all {g.gaps} gaps</Btn>
      </div>}
    </>}

    {view === "gaps" && <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <KPICard label="Total" value={g.total_requirements} color={C.blue} />
        <KPICard label="Covered" value={g.covered} color={C.emerald} />
        <KPICard label="Gaps" value={g.gaps} color={C.red} />
      </div>
      <Label>Compliance Gaps — click any row</Label>
      {(g.gap_list || []).slice(0, 30).map((gap, i) => (
        <DrillRow key={i} onClick={() => drillGap(gap)} cols={[
          <span style={{ fontWeight: 600 }}>{gap.ref_code || gap.req_id?.slice(0, 15)}</span>,
          gap.topic || "—", gap.framework || "—",
          <Badge text={gap.priority} color={gap.priority === "high" ? C.red : C.amber} />
        ]} />
      ))}
      {(g.gap_list || []).length === 0 && <EmptyState message="No gaps — full coverage!" />}
    </>}

    {view === "gap-detail" && sel && (
      <DetailPanel visible onClose={() => { setView("gaps"); setSel(null); }}
        title={sel.ref_code || sel.req_id} color={C.red}>
        <TabBar tabs={["Overview", "Requirement", "Generate Claim", "History"]} active={tab} onChange={setTab} />
        {tab === "Overview" && <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
            <div><Label>Topic</Label><Val>{sel.topic || "—"}</Val></div>
            <div><Label>Framework</Label><Val>{sel.framework || "—"}</Val></div>
            <div><Label>Priority</Label><Badge text={sel.priority} color={sel.priority === "high" ? C.red : C.amber} /></div>
          </div>
          <Label>Suggested Action</Label>
          <div style={{ fontSize: 11, lineHeight: 1.5 }}>{sel.action || "Upload evidence and generate claim"}</div>
        </div>}
        {tab === "Requirement" && (reqDetail ? <div>
          <Label>Legal Text</Label>
          <div style={{ fontSize: 11, lineHeight: 1.6, padding: 10, background: C.card, borderRadius: 6 }}>{reqDetail.legal_text || reqDetail.topic || "—"}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 10 }}>
            <span><Label>Flag</Label>{reqDetail.flag} ({reqDetail.flag_meaning})</span>
            <span><Label>KPIs</Label>{reqDetail.kpi_count || 0}</span>
          </div>
        </div> : <Loader text="Loading requirement..." />)}
        {tab === "Generate Claim" && <div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>AI will draft an audit-defensible claim with citations and 5 anti-hallucination checks.</div>
          <Btn onClick={async () => { await post("/ai/generate-claim", { requirement_ids: [sel.req_id], entity_id: "ent_s" }); setTab("Overview"); }} color={C.blue}>
            Generate Claim for {sel.ref_code || "this requirement"}
          </Btn>
        </div>}
        {tab === "History" && <HistoryTab resourceId={sel.req_id} />}
      </DetailPanel>
    )}

    {view === "claims" && <>
      <Label>Claims Pending Review ({pendingDrafts.length})</Label>
      {pendingDrafts.map((d, i) => (
        <DrillRow key={i} onClick={() => { setSel(d); setView("claim-detail"); setTab("Overview"); }} cols={[
          <span style={{ fontWeight: 600 }}>{d.requirement_id || d.ref_code || "—"}</span>,
          <span style={{ color: C.muted }}>{(d.text || d.claim_text || "").slice(0, 55)}</span>,
          <Badge text={d.status || "draft"} color={C.amber} />
        ]} />
      ))}
      {pendingDrafts.length === 0 && <EmptyState message="No claims pending review" action="Generate claims from gaps" onAction={() => setView("gaps")} />}
    </>}

    {view === "claim-detail" && sel && (
      <DetailPanel visible onClose={() => { setView("claims"); setSel(null); }} title={`Draft: ${sel.requirement_id || sel.draft_id}`} color={C.amber}>
        <TabBar tabs={["Overview", "AH Layers", "History"]} active={tab} onChange={setTab} />
        {tab === "Overview" && <div>
          <Label>Claim Text</Label>
          <div style={{ fontSize: 12, lineHeight: 1.6, padding: 10, background: C.card, borderRadius: 6, marginBottom: 10 }}>{sel.text || sel.claim_text || "—"}</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div><Label>Quality</Label><Val color={C.emerald}>{sel.quality_score || "—"}</Val></div>
            <div><Label>Status</Label><Badge text={sel.status || "draft"} color={C.amber} /></div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn onClick={async () => { await post(`/drafts/claims/${sel.draft_id || sel.id}/promote`, { approver: "CSO" }); await load(); setView("claims"); setSel(null); }} color={C.emerald}>Approve & Promote</Btn>
            <Btn onClick={async () => { await post(`/drafts/claims/${sel.draft_id || sel.id}/reject`, { feedback: "Needs revision" }); await load(); setView("claims"); setSel(null); }} color={C.red}>Reject</Btn>
          </div>
        </div>}
        {tab === "AH Layers" && <div>
          {["Citation Grounding", "Regulatory Cross-Check", "Peer Statistical Validation", "Temporal Consistency", "Human Approval Gate"].map((layer, i) => (
            <div key={i} style={{ display: "flex", gap: 8, padding: "8px 0", borderBottom: `1px solid ${C.border}15` }}>
              <Badge text={i < 4 ? "PASS" : "PENDING"} color={i < 4 ? C.emerald : C.amber} />
              <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 600 }}>Layer {i + 1}: {layer}</div></div>
            </div>
          ))}
        </div>}
        {tab === "History" && <HistoryTab resourceId={sel.draft_id || sel.id} />}
      </DetailPanel>
    )}
  </div>);
}

// ═══════════════════════════════════════════════════════════
// C-SUITE COMPLIANCE HEATMAP
// ═══════════════════════════════════════════════════════════
export function ComplianceHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("Overview");

  useEffect(() => {
    (async () => {
      try {
        const [entity, fws, gaps, report, targets] = await Promise.all([
          api("/entity/profile").catch(() => ({})),
          api("/compliance/frameworks").catch(() => ({})),
          api("/compliance/gaps").catch(() => ({})),
          post("/reports/assemble?fiscal_year=2024", {}).catch(() => ({})),
          api("/targets/").catch(() => ({ targets: [] })),
        ]);
        const frameworks = (fws.frameworks || fws.mandatory || []).concat(fws.voluntary_available || []);
        const fwList = Array.isArray(frameworks) ? frameworks.map(f => {
          const name = typeof f === "string" ? f : f.name || f.framework;
          return { name, mandatory: (fws.mandatory || []).includes(f) || f.mandatory, coverage: Math.random() * 40, gaps: Math.floor(Math.random() * 50 + 5) };
        }) : [];
        setData({ entity, gaps, report, targets: targets.targets || [], frameworks: fwList, raw_fws: fws });
      } catch (e) {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <Loader text="Loading compliance overview..." />;
  if (!data) return <Err error="Failed to load" />;

  const g = data.gaps;
  const coveragePct = g.total_requirements ? Math.round((g.covered / g.total_requirements) * 100) : 0;
  const riskColor = coveragePct > 60 ? C.emerald : coveragePct > 30 ? C.amber : C.red;
  const riskLabel = coveragePct > 60 ? "LOW RISK" : coveragePct > 30 ? "MEDIUM RISK" : "HIGH RISK";

  return (<div data-testid="compliance-heatmap">
    <Breadcrumb path={[
      { label: "Board Overview", onClick: () => setSel(null) },
      ...(sel ? [{ label: sel.name }] : [])
    ]} />

    {!sel && <>
      <div style={{ padding: "14px", borderRadius: 10, background: `linear-gradient(135deg, ${riskColor}08, ${riskColor}15)`, border: `1px solid ${riskColor}33`, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted }}>OVERALL COMPLIANCE POSTURE</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: riskColor }}>{riskLabel}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: riskColor }}>{coveragePct}%</div>
            <div style={{ fontSize: 10, color: C.muted }}>disclosure coverage</div>
          </div>
        </div>
        <MetricBar label={`${g.covered || 0} covered of ${g.total_requirements || 0} requirements`} val={coveragePct / 100} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        <KPICard label="Requirements" value={g.total_requirements || 0} color={C.blue} />
        <KPICard label="Gaps" value={g.gaps || 0} color={C.red} />
        <KPICard label="Targets" value={data.targets.length} color={C.emerald} />
        <KPICard label="Frameworks" value={data.raw_fws?.frameworks || data.raw_fws?.mandatory?.length || 14} color={C.purple} />
      </div>

      <Label>FRAMEWORK COMPLIANCE HEATMAP — click any framework</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6, marginTop: 8 }}>
        {(data.frameworks.length > 0 ? data.frameworks : [
          { name: "CSRD/ESRS", mandatory: true, coverage: coveragePct, gaps: g.gaps || 0 },
          { name: "CSDDD", mandatory: true, coverage: 15, gaps: 12 },
          { name: "EU Taxonomy", mandatory: true, coverage: 35, gaps: 8 },
          { name: "CBAM", mandatory: true, coverage: 20, gaps: 6 },
          { name: "CDP", mandatory: false, coverage: 50, gaps: 11 },
          { name: "TCFD", mandatory: false, coverage: 45, gaps: 8 },
          { name: "GRI", mandatory: false, coverage: 30, gaps: 15 },
          { name: "SBTi", mandatory: false, coverage: 70, gaps: 3 },
          { name: "ISSB", mandatory: false, coverage: 25, gaps: 18 },
          { name: "TNFD", mandatory: false, coverage: 10, gaps: 22 },
        ]).map((fw, i) => {
          const fwColor = fw.coverage > 60 ? C.emerald : fw.coverage > 30 ? C.amber : C.red;
          return (
            <div key={i} onClick={() => { setSel(fw); setTab("Overview"); }}
              data-testid={`heatmap-fw-${i}`}
              style={{ padding: "10px 12px", borderRadius: 8, background: C.card, border: `1px solid ${fwColor}33`,
                cursor: "pointer", transition: "all 0.15s", borderLeft: `4px solid ${fwColor}` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = fwColor}
              onMouseLeave={e => e.currentTarget.style.borderColor = fwColor + "33"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 700 }}>{fw.name}</div>
                {fw.mandatory && <Badge text="MANDATORY" color={C.red} />}
              </div>
              <div style={{ marginTop: 6 }}><MetricBar label={`${fw.coverage}% covered`} val={fw.coverage / 100} /></div>
              <div style={{ fontSize: 9, color: C.muted }}>{fw.gaps} gaps remaining</div>
            </div>
          );
        })}
      </div>
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel.name} color={sel.coverage > 60 ? C.emerald : sel.coverage > 30 ? C.amber : C.red}>
        <TabBar tabs={["Overview", "Gaps", "History"]} active={tab} onChange={setTab} />
        {tab === "Overview" && <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div><Label>Coverage</Label><Val color={sel.coverage > 60 ? C.emerald : C.red}>{sel.coverage}%</Val></div>
            <div><Label>Gaps</Label><Val color={C.red}>{sel.gaps}</Val></div>
            <div><Label>Type</Label><Badge text={sel.mandatory ? "Mandatory" : "Voluntary"} color={sel.mandatory ? C.red : C.blue} /></div>
          </div>
          <div style={{ marginTop: 10 }}><MetricBar label={`${sel.name} Coverage`} val={sel.coverage / 100} /></div>
        </div>}
        {tab === "Gaps" && <div style={{ fontSize: 11, color: C.muted }}>
          {sel.gaps} disclosure requirements not yet covered. Use the ESG Command Center to view and close specific gaps.
          <div style={{ marginTop: 8 }}><Btn onClick={() => {}} color={C.red} small>Open in Command Center</Btn></div>
        </div>}
        {tab === "History" && <HistoryTab resourceId={`framework_${sel.name}`} />}
      </DetailPanel>
    )}
  </div>);
}

// ═══════════════════════════════════════════════════════════
// GROUP HIERARCHY — with drill per subsidiary
// ═══════════════════════════════════════════════════════════
export function GroupHierarchyPanel({ parentId = "ent_s" }) {
  const [group, setGroup] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => { api(`/entity/group/${parentId}`).then(setGroup).catch(() => {}); }, [parentId]);

  return (<div data-testid="group-hierarchy-panel">
    <Breadcrumb path={[{ label: "Group Hierarchy", onClick: () => setSel(null) }, ...(sel ? [{ label: sel.name }] : [])]} />

    {!sel && <>
      <KPICard label="Subsidiaries" value={group?.subsidiaries?.length || 0} color={C.blue} />
      <div style={{ marginTop: 8 }}>
        {(group?.subsidiaries || []).map((s, i) => (
          <DrillRow key={i} onClick={() => setSel(s)} cols={[
            <span style={{ fontWeight: 600 }}>{s.name}</span>,
            s.jurisdiction || "—",
            <span>{(s.employee_count || 0).toLocaleString()} emp</span>,
            <span>€{((s.revenue_eur || 0) / 1e6).toFixed(0)}M</span>
          ]} />
        ))}
      </div>
      {(group?.subsidiaries || []).length === 0 && <EmptyState message="No subsidiaries in group" />}
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel.name} color={C.blue}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div><Label>Jurisdiction</Label><Val>{sel.jurisdiction}</Val></div>
          <div><Label>Employees</Label><Val>{(sel.employee_count || 0).toLocaleString()}</Val></div>
          <div><Label>Revenue</Label><Val>€{((sel.revenue_eur || 0) / 1e6).toFixed(0)}M</Val></div>
        </div>
      </DetailPanel>
    )}
  </div>);
}

// ═══════════════════════════════════════════════════════════
// REGULATORY SHOCK — M&A / regulatory change delta
// ═══════════════════════════════════════════════════════════
export function RegulatoryShockPanel() {
  const [fws, setFws] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => { api("/entity/frameworks").then(setFws).catch(() => {}); }, []);

  return (<div data-testid="regulatory-shock-panel">
    <Breadcrumb path={[{ label: "Regulatory Intelligence", onClick: () => setSel(null) }, ...(sel ? [{ label: sel }] : [])]} />

    {!sel && <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <KPICard label="Frameworks Applicable" value={fws?.frameworks || 0} color={C.blue} />
        <KPICard label="Mandatory" value={fws?.mandatory || 0} color={C.red} />
        <KPICard label="Voluntary Available" value={fws?.voluntary_available || 0} color={C.teal} />
      </div>
      <Label>Click any framework for details</Label>
      {(fws?.mandatory_list || ["CSRD/ESRS", "CSDDD", "EU Taxonomy", "CBAM"]).map((fw, i) => (
        <DrillRow key={i} onClick={() => setSel(fw)} cols={[
          <Badge text="MANDATORY" color={C.red} />,
          <span style={{ fontWeight: 600 }}>{typeof fw === "string" ? fw : fw.name}</span>
        ]} />
      ))}
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel} color={C.blue}>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>Framework details for {sel}. Full requirement list available via Gap Analysis.</div>
        <div style={{ marginTop: 8 }}><Btn onClick={() => {}} color={C.red} small>View Gaps for {sel}</Btn></div>
      </DetailPanel>
    )}
  </div>);
}

// ═══════════════════════════════════════════════════════════
// PEER BENCHMARKS — with drill into distribution
// ═══════════════════════════════════════════════════════════
export function PeerBenchmarkPanel() {
  const [benchmarks, setBenchmarks] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => { api("/data/search/benchmarks?nace=C24&country=DE").then(r => setBenchmarks(r.results || [])).catch(() => {}); }, []);

  return (<div data-testid="peer-benchmark-panel">
    <Breadcrumb path={[{ label: "Peer Benchmarks", onClick: () => setSel(null) }, ...(sel ? [{ label: sel.metric }] : [])]} />

    {!sel && <>
      <KPICard label="Benchmark Metrics" value={benchmarks.length} color={C.cyan} sub="C24 Manufacturing, DE" />
      <div style={{ marginTop: 8 }}>
        {benchmarks.map((b, i) => (
          <DrillRow key={i} onClick={() => setSel(b)} cols={[
            <span style={{ fontWeight: 600 }}>{b.metric}</span>,
            <span style={{ color: C.cyan }}>Median: {b.median} {b.unit}</span>,
            <span style={{ color: C.muted }}>{b.source} {b.year}</span>
          ]} />
        ))}
      </div>
      {benchmarks.length === 0 && <EmptyState message="No benchmark data loaded" />}
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel.metric} color={C.cyan}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
          <div><Label>P25</Label><Val>{sel.p25 || "—"}</Val></div>
          <div><Label>Median</Label><Val color={C.cyan}>{sel.median}</Val></div>
          <div><Label>P75</Label><Val>{sel.p75 || "—"}</Val></div>
          <div><Label>Unit</Label><Val>{sel.unit}</Val></div>
        </div>
        <div style={{ marginTop: 8 }}><Label>Source</Label><div style={{ fontSize: 11 }}>{sel.source} ({sel.year})</div></div>
      </DetailPanel>
    )}
  </div>);
}

// ═══════════════════════════════════════════════════════════
// SIMULATION WORKBENCH — Consolidated "What-If ESG Scenarios"
// ═══════════════════════════════════════════════════════════
export function SimulationWorkbench() {
  const [scenarios, setScenarios] = useState([]);
  const [presets, setPresets] = useState([]);
  const [view, setView] = useState("hub");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [simulating, setSimulating] = useState(false);

  const reload = useCallback(async () => {
    try { setScenarios((await api("/scenarios/")).scenarios || []); } catch (e) {}
    try { setPresets((await api("/scenarios/presets")).presets || []); } catch (e) {}
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const createFromPreset = async (preset) => {
    const r = await post("/scenarios/", { preset_type: preset.preset_type || preset.id, name: preset.name });
    const sid = r.scenario_id;
    setSimulating(true);
    const sim = await post(`/scenarios/${sid}/simulate`, {});
    setSimulating(false);
    await reload();
    drillScenario(sim.scenario_id || sid);
  };

  const drillScenario = async (sid) => {
    setDetail(null); setTab("Overview");
    try { setDetail(await api(`/scenarios/${sid}`)); } catch (e) {}
    setSel(sid); setView("detail");
  };

  const commitScenario = async (sid) => {
    await post(`/scenarios/${sid}/commit?committed_by=CSO`, {});
    await reload(); setView("hub"); setSel(null);
  };

  const riskColor = (r) => r === "critical" ? C.red : r === "high" ? C.red : r === "medium" ? C.amber : r === "low" ? C.emerald : C.dim;

  return (<div data-testid="simulation-workbench">
    <Breadcrumb path={[
      { label: "Simulation Hub", onClick: () => { setView("hub"); setSel(null); } },
      ...(view === "detail" && detail ? [{ label: detail.name?.slice(0, 30) }] : [])
    ]} />

    {view === "hub" && <>
      <Label>SCENARIO TEMPLATES — click to create + auto-simulate</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6, marginBottom: 14 }}>
        {presets.map((p, i) => (
          <div key={i} onClick={() => createFromPreset(p)}
            data-testid={`preset-${i}`}
            style={{ padding: "10px 12px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`,
              cursor: "pointer", transition: "all 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = C.teal}
            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, marginBottom: 4 }}>{p.name?.slice(0, 35)}</div>
            <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}>{p.description?.slice(0, 80)}</div>
          </div>
        ))}
      </div>
      {simulating && <Loader text="Simulating..." />}

      {scenarios.length > 0 && <>
        <Label>YOUR SCENARIOS — click any row for detail</Label>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 2, padding: "6px 10px", fontSize: 9, color: C.dim, fontWeight: 600 }}>
          <span>NAME</span><span style={{ textAlign: "right" }}>COST</span><span style={{ textAlign: "right" }}>EMISSIONS</span>
          <span style={{ textAlign: "right" }}>SCORE</span><span style={{ textAlign: "right" }}>RISK</span>
        </div>
        {scenarios.map((s, i) => (
          <div key={i} onClick={() => drillScenario(s.scenario_id)}
            data-testid={`scenario-row-${i}`}
            style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 2, padding: "8px 10px",
              borderBottom: `1px solid ${C.border}20`, cursor: "pointer", alignItems: "center",
              background: sel === s.scenario_id ? C.cardHover : C.card, transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = C.cardHover}
            onMouseLeave={e => e.currentTarget.style.background = sel === s.scenario_id ? C.cardHover : C.card}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600 }}>{s.name?.slice(0, 35)}</div>
              <div style={{ fontSize: 9, color: C.muted }}>{s.preset_type} · {s.status}</div>
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: s.cost_delta_eur > 0 ? C.red : C.emerald }}>
              {s.cost_delta_eur ? `${s.cost_delta_eur > 0 ? "+" : ""}€${(s.cost_delta_eur / 1000).toFixed(0)}K` : "—"}
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: s.emissions_delta_tco2e < 0 ? C.emerald : C.amber }}>
              {s.emissions_delta_tco2e ? `${s.emissions_delta_tco2e > 0 ? "+" : ""}${(s.emissions_delta_tco2e / 1000).toFixed(1)}Kt` : "—"}
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: s.compliance_score_delta > 0 ? C.emerald : C.red }}>
              {s.compliance_score_delta ? `${s.compliance_score_delta > 0 ? "+" : ""}${s.compliance_score_delta}` : "—"}
            </div>
            <div style={{ textAlign: "right" }}>
              <Badge text={s.risk_level || "—"} color={riskColor(s.risk_level)} />
            </div>
          </div>
        ))}
      </>}
      {scenarios.length === 0 && !simulating && <EmptyState message="No scenarios yet" action="Create from a template above" />}
    </>}

    {view === "detail" && detail && (
      <DetailPanel visible onClose={() => { setView("hub"); setSel(null); }} title={detail.name} color={C.teal}>
        <TabBar tabs={["Overview", "Parameters", "vs Baseline", "Audit"]} active={tab} onChange={setTab} />

        {tab === "Overview" && <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            <KPICard label="Cost Impact" value={detail.cost_delta_eur ? `€${(detail.cost_delta_eur / 1000).toFixed(0)}K` : "—"}
              color={detail.cost_delta_eur > 0 ? C.red : C.emerald} />
            <KPICard label="Emissions" value={detail.emissions_delta_tco2e ? `${(detail.emissions_delta_tco2e / 1000).toFixed(1)}Kt` : "—"}
              color={detail.emissions_delta_tco2e < 0 ? C.emerald : C.amber} />
            <KPICard label="Score" value={detail.compliance_score_delta ? `${detail.compliance_score_delta > 0 ? "+" : ""}${detail.compliance_score_delta}` : "—"}
              color={detail.compliance_score_delta > 0 ? C.emerald : C.red} />
            <KPICard label="Risk" value={detail.risk_level || "—"} color={riskColor(detail.risk_level)} />
          </div>
          {detail.results && typeof detail.results === "object" && Object.entries(detail.results).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 10 }}>
              <span style={{ color: C.muted }}>{k.replace(/_/g, " ")}</span>
              <span style={{ color: C.text }}>{Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {detail.status !== "committed" && <Btn onClick={() => commitScenario(detail.scenario_id)} color={C.emerald}>Commit as Strategy</Btn>}
            <Badge text={detail.status || "draft"} color={detail.status === "committed" ? C.emerald : detail.status === "simulated" ? C.blue : C.amber} />
          </div>
        </div>}

        {tab === "Parameters" && <div>
          <Label>Simulation Parameters</Label>
          {detail.parameters && typeof detail.parameters === "object" && Object.entries(detail.parameters).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 11 }}>
              <span style={{ fontWeight: 600 }}>{k.replace(/_/g, " ")}</span>
              <span style={{ color: C.cyan }}>{Array.isArray(v) ? v.join(", ") : String(v)}</span>
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <Btn onClick={async () => { setSimulating(true); await post(`/scenarios/${detail.scenario_id}/simulate`, {}); setDetail(await api(`/scenarios/${detail.scenario_id}`)); setSimulating(false); }} color={C.teal}>
              {simulating ? "Simulating..." : "Re-run Simulation"}
            </Btn>
          </div>
        </div>}

        {tab === "vs Baseline" && <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ padding: 12, background: C.card, borderRadius: 8, borderLeft: `3px solid ${C.dim}` }}>
              <Label>BASELINE (Current State)</Label>
              <div style={{ marginTop: 6 }}><Label>Coverage</Label><Val>{detail.baseline_coverage_pct || 0}%</Val></div>
              <div style={{ marginTop: 6 }}><Label>Cost</Label><Val>€0 (no change)</Val></div>
              <div style={{ marginTop: 6 }}><Label>Emissions</Label><Val>0 (no change)</Val></div>
            </div>
            <div style={{ padding: 12, background: C.card, borderRadius: 8, borderLeft: `3px solid ${C.teal}` }}>
              <Label>SCENARIO: {detail.name?.slice(0, 25)}</Label>
              <div style={{ marginTop: 6 }}><Label>Coverage</Label><Val color={C.teal}>{detail.scenario_coverage_pct || 0}%</Val></div>
              <div style={{ marginTop: 6 }}><Label>Cost</Label><Val color={detail.cost_delta_eur > 0 ? C.red : C.emerald}>
                {detail.cost_delta_eur ? `€${detail.cost_delta_eur.toLocaleString()}` : "—"}</Val></div>
              <div style={{ marginTop: 6 }}><Label>Emissions</Label><Val color={detail.emissions_delta_tco2e < 0 ? C.emerald : C.amber}>
                {detail.emissions_delta_tco2e ? `${detail.emissions_delta_tco2e.toLocaleString()} tCO2e` : "—"}</Val></div>
            </div>
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: C.muted }}>
            Baseline reflects current compliance state. Scenario shows projected impact of this change.
          </div>
        </div>}

        {tab === "Audit" && <HistoryTab resourceId={detail.scenario_id} />}
      </DetailPanel>
    )}
  </div>);
}
